import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Divider,
    Stack,
    Alert,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import ResumenInventario from "../ui/ResumenInventario";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { INVENTARIO_ESTADO } from "../../constants";
import { BOTON_SECUNDARIO_SX } from "../../../../components/ui/buttonStyles";

/**
 * Modal de confirmación para eliminar una lista de inventario (en los estados
 * de ESTADOS_ELIMINABLES; si está en curso avisa de que se pierde lo contado).
 * Mismo patrón responsive que
 * rangos/components/modals/ModalEliminarRango.js: no gestiona toasts ni
 * cierra por su cuenta, permanece operativo si el borrado falla.
 */
export default function ModalEliminarInventario({ show, onHide, onConfirm, inventario, loading = false }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <Dialog
            open={show}
            onClose={loading ? undefined : onHide}
            maxWidth="xs"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: { borderRadius: isMobile ? 0 : 2, boxShadow: "0 8px 32px rgba(0,0,0,0.12)", m: isMobile ? 0 : 2 },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    pb: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 3 },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                    <Box
                        sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: "50%",
                            backgroundColor: "#fff5f5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <WarningAmberIcon sx={{ color: "#dc3545", fontSize: { xs: 20, sm: 24 } }} />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: "#212529", fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
                    >
                        {isMobile ? "Eliminar" : "Confirmar eliminación"}
                    </Typography>
                </Box>
                <IconButton onClick={onHide} disabled={loading} size="small" sx={{ color: "#6c757d" }}>
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent
                sx={{ pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, px: { xs: 2, sm: 3 }, flex: isMobile ? 1 : "initial" }}
            >
                <Stack spacing={{ xs: 2, sm: 2.5 }}>
                    <Typography sx={{ fontSize: { xs: 14, sm: 15 }, color: "#495057", lineHeight: 1.6 }}>
                        ¿Estás seguro de que deseas <strong style={{ color: "#dc3545" }}>eliminar</strong> esta lista
                        de inventario?
                    </Typography>

                    {inventario && <ResumenInventario inventario={inventario} />}

                    {inventario?.estado === INVENTARIO_ESTADO.EN_CURSO && (
                        <Alert severity="warning">
                            Este inventario está en curso: se perderán las cantidades que el preparador ya haya contado.
                        </Alert>
                    )}

                    <Alert
                        severity="error"
                        sx={{ backgroundColor: "#fff5f5", color: "#842029", border: "1px solid #f8d7da", "& .MuiAlert-icon": { color: "#dc3545" } }}
                    >
                        <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 600 }}>Esta acción no se puede deshacer</Typography>
                    </Alert>
                </Stack>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: { xs: 2, sm: 2.5 }, gap: { xs: 1, sm: 1.5 }, flexDirection: { xs: "column-reverse", sm: "row" } }}>
                <Button
                    variant="outlined"
                    onClick={onHide}
                    fullWidth={isMobile}
                    disabled={loading}
                    sx={{ ...BOTON_SECUNDARIO_SX, height: "auto", px: { xs: 2, sm: 2.5 }, py: { xs: 1.25, sm: 1 }, fontSize: { xs: 14, sm: 15 } }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={onConfirm}
                    fullWidth={isMobile}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <DeleteForeverIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        backgroundColor: "#dc3545",
                        "&:hover": { backgroundColor: "#bb2d3b" },
                        "&:disabled": { backgroundColor: "#6c757d", color: "#fff" },
                    }}
                >
                    {loading ? "Eliminando..." : isMobile ? "Eliminar" : "Eliminar inventario"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
